import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { uploadData, remove } from "aws-amplify/storage";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/browser";

import { useUserContext } from "./UserContext";
import { iProvider } from "../types";
import {
    Classification,
    SkillCategory,
    SkillTask,
    SubCategory,
    SkillLevel,
    SkillAssignment,
    UserFile,
    ChangeLogEntry,
    CreateCategoryInput,
    CreateTaskInput,
    CreateSubCategoryInput,
    CreateSkillLevelInput,
    AssignSkillInput,
    CreateChangeLogInput,
} from "../types/userModuleTypes";
import { sortByName } from "../utils/userModuleUtils";
import {
    classificationsByCompanyId,
    skillCategoriesByCompanyId,
    skillTasksByCompanyId,
    subCategoriesByCompanyId,
    skillLevelsByCompanyId,
    skillAssignmentsByCompanyId,
    skillAssignmentsByUserId,
    userFilesByUserId,
    changeLogEntriesByUserId,
    createClassification as createClassificationMutation,
    updateClassification as updateClassificationMutation,
    deleteClassification as deleteClassificationMutation,
    createSkillCategory as createSkillCategoryMutation,
    updateSkillCategory as updateSkillCategoryMutation,
    deleteSkillCategory as deleteSkillCategoryMutation,
    createSkillTask as createSkillTaskMutation,
    updateSkillTask as updateSkillTaskMutation,
    deleteSkillTask as deleteSkillTaskMutation,
    createSubCategory as createSubCategoryMutation,
    updateSubCategory as updateSubCategoryMutation,
    deleteSubCategory as deleteSubCategoryMutation,
    createSkillLevel as createSkillLevelMutation,
    updateSkillLevel as updateSkillLevelMutation,
    deleteSkillLevel as deleteSkillLevelMutation,
    createSkillAssignment as createSkillAssignmentMutation,
    updateSkillAssignment as updateSkillAssignmentMutation,
    deleteSkillAssignment as deleteSkillAssignmentMutation,
    createUserFile as createUserFileMutation,
    deleteUserFile as deleteUserFileMutation,
    updateUserFile as updateUserFileMutation,
    createChangeLogEntry as createChangeLogEntryMutation,
} from "../graphql/userModuleQueries";

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

interface UserModuleContextValue {
    // Skillset library data (company-wide)
    classifications: Classification[];
    categories: SkillCategory[];
    tasks: SkillTask[];
    subCategories: SubCategory[];
    skillLevels: SkillLevel[];

    // User-specific data (fetched on demand)
    skillAssignments: SkillAssignment[];
    userFiles: UserFile[];
    changeLogEntries: ChangeLogEntry[];

    // Loading state
    loading: boolean;

    // User-specific fetch functions
    fetchSkillAssignments: (userId: string) => Promise<void>;
    fetchUserFiles: (userId: string) => Promise<void>;
    fetchChangeLog: (userId: string) => Promise<void>;

    // Classification CRUD
    createClassification: (name: string) => Promise<void>;
    updateClassification: (id: string, name: string) => Promise<void>;
    deleteClassification: (id: string) => Promise<void>;

    // Category CRUD
    createCategory: (input: CreateCategoryInput) => Promise<void>;
    updateCategory: (id: string, name: string, allowMachineNumber?: boolean) => Promise<void>;
    deleteCategory: (id: string) => Promise<void>;

    // Task CRUD
    createTask: (input: CreateTaskInput) => Promise<void>;
    updateTask: (id: string, name: string) => Promise<void>;
    deleteTask: (id: string) => Promise<void>;

    // SubCategory CRUD
    createSubCategory: (input: CreateSubCategoryInput) => Promise<SubCategory>;
    updateSubCategory: (id: string, name: string) => Promise<void>;
    deleteSubCategory: (id: string) => Promise<void>;

    // SkillLevel CRUD
    createSkillLevel: (input: CreateSkillLevelInput) => Promise<void>;
    updateSkillLevel: (id: string, name: string, sortOrder?: number, description?: string, color?: string) => Promise<void>;
    deleteSkillLevel: (id: string) => Promise<void>;

    // SkillAssignment CRUD
    assignSkill: (input: AssignSkillInput) => Promise<void>;
    updateSkillAssignment: (id: string, updates: Partial<Omit<AssignSkillInput, "userId" | "taskId" | "companyId">>) => Promise<void>;
    deleteSkillAssignment: (id: string) => Promise<void>;

    // UserFile operations
    uploadUserFile: (userId: string, file: File) => Promise<void>;
    deleteUserFile: (fileId: string, s3Path: string) => Promise<void>;
    renameUserFile: (fileId: string, newFileName: string) => Promise<void>;

    // ChangeLog
    createChangeLogEntry: (input: CreateChangeLogInput) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Default context value
// ---------------------------------------------------------------------------

const defaultContextValue: UserModuleContextValue = {
    classifications: [],
    categories: [],
    tasks: [],
    subCategories: [],
    skillLevels: [],
    skillAssignments: [],
    userFiles: [],
    changeLogEntries: [],
    loading: false,
    fetchSkillAssignments: async () => { },
    fetchUserFiles: async () => { },
    fetchChangeLog: async () => { },
    createClassification: async () => { },
    updateClassification: async () => { },
    deleteClassification: async () => { },
    createCategory: async () => { },
    updateCategory: async () => { },
    deleteCategory: async () => { },
    createTask: async () => { },
    updateTask: async () => { },
    deleteTask: async () => { },
    createSubCategory: async () => { return {} as SubCategory; },
    updateSubCategory: async () => { },
    deleteSubCategory: async () => { },
    createSkillLevel: async () => { },
    updateSkillLevel: async () => { },
    deleteSkillLevel: async () => { },
    assignSkill: async () => { },
    updateSkillAssignment: async () => { },
    deleteSkillAssignment: async () => { },
    uploadUserFile: async () => { },
    deleteUserFile: async () => { },
    renameUserFile: async () => { },
    createChangeLogEntry: async () => { },
};

export const UserModuleContext = createContext<UserModuleContextValue>(defaultContextValue);

// eslint-disable-next-line react-refresh/only-export-components
export const useUserModule = () => useContext(UserModuleContext);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const UserModuleProvider = ({ children }: iProvider) => {
    const { companyUser, companyOwner } = useUserContext();

    const [classifications, setClassifications] = useState<Classification[]>([]);
    const [categories, setCategories] = useState<SkillCategory[]>([]);
    const [tasks, setTasks] = useState<SkillTask[]>([]);
    const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
    const [skillLevels, setSkillLevels] = useState<SkillLevel[]>([]);
    const [skillAssignments, setSkillAssignments] = useState<SkillAssignment[]>([]);
    const [userFiles, setUserFiles] = useState<UserFile[]>([]);
    const [changeLogEntries, setChangeLogEntries] = useState<ChangeLogEntry[]>([]);
    const [loading, setLoading] = useState(false);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId;

    // -----------------------------------------------------------------------
    // Fetch company-wide skillset library data on mount
    // -----------------------------------------------------------------------

    const fetchLibraryData = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const [
                classificationsData,
                categoriesData,
                tasksData,
                subCategoriesData,
                skillLevelsData,
                skillAssignmentsData,
            ] = await Promise.all([
                classificationsByCompanyId(companyId),
                skillCategoriesByCompanyId(companyId),
                skillTasksByCompanyId(companyId),
                subCategoriesByCompanyId(companyId),
                skillLevelsByCompanyId(companyId),
                skillAssignmentsByCompanyId(companyId),
            ]);

            setClassifications(sortByName(classificationsData));
            setCategories(sortByName(categoriesData));
            setTasks(sortByName(tasksData));
            setSubCategories(sortByName(subCategoriesData));
            setSkillLevels(skillLevelsData);
            setSkillAssignments(skillAssignmentsData);
        } catch (error) {
            console.error("Failed to fetch user module library data:", error);
            Sentry.captureException(error);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (companyId) {
            fetchLibraryData();
        } else {
            setClassifications([]);
            setCategories([]);
            setTasks([]);
            setSubCategories([]);
            setSkillLevels([]);
        }
    }, [companyId, fetchLibraryData]);

    // -----------------------------------------------------------------------
    // User-specific fetch functions
    // -----------------------------------------------------------------------

    const fetchSkillAssignments = useCallback(async (userId: string) => {
        try {
            const data = await skillAssignmentsByUserId(userId);
            setSkillAssignments(data);
        } catch (error) {
            console.error("Failed to fetch skill assignments:", error);
            Sentry.captureException(error);
        }
    }, []);

    const fetchUserFiles = useCallback(async (userId: string) => {
        try {
            const data = await userFilesByUserId(userId);
            setUserFiles(data);
        } catch (error) {
            console.error("Failed to fetch user files:", error);
            Sentry.captureException(error);
        }
    }, []);

    const fetchChangeLog = useCallback(async (userId: string) => {
        try {
            const data = await changeLogEntriesByUserId(userId);
            setChangeLogEntries(data);
        } catch (error) {
            console.error("Failed to fetch change log:", error);
            Sentry.captureException(error);
        }
    }, []);

    // -----------------------------------------------------------------------
    // Classification CRUD
    // -----------------------------------------------------------------------

    const createClassification = useCallback(async (name: string) => {
        if (!companyId) return;
        try {
            const result = await createClassificationMutation({ companyId, name });
            setClassifications((prev) => sortByName([...prev, result]));
        } catch (error) {
            console.error("Failed to create classification:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, [companyId]);

    const updateClassification = useCallback(async (id: string, name: string) => {
        try {
            const result = await updateClassificationMutation({ id, name });
            setClassifications((prev) =>
                sortByName(prev.map((c) => (c.id === id ? result : c)))
            );
        } catch (error) {
            console.error("Failed to update classification:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const deleteClassification = useCallback(async (id: string) => {
        try {
            // Cascade delete: find associated categories
            const associatedCategories = categories.filter((c) => c.classificationId === id);
            const associatedCategoryIds = associatedCategories.map((c) => c.id);

            // Find tasks under those categories
            const associatedTasks = tasks.filter((t) => associatedCategoryIds.includes(t.categoryId));
            const associatedTaskIds = associatedTasks.map((t) => t.id);

            // Find skill assignments for those tasks
            const associatedAssignments = skillAssignments.filter((a) => associatedTaskIds.includes(a.taskId));

            // Delete skill assignments
            for (const assignment of associatedAssignments) {
                await deleteSkillAssignmentMutation(assignment.id);
            }

            // Delete tasks
            for (const task of associatedTasks) {
                await deleteSkillTaskMutation(task.id);
            }

            // Delete categories
            for (const category of associatedCategories) {
                await deleteSkillCategoryMutation(category.id);
            }

            // Delete the classification itself
            await deleteClassificationMutation(id);

            // Update local state
            setSkillAssignments((prev) => prev.filter((a) => !associatedTaskIds.includes(a.taskId)));
            setTasks((prev) => prev.filter((t) => !associatedCategoryIds.includes(t.categoryId)));
            setCategories((prev) => prev.filter((c) => c.classificationId !== id));
            setClassifications((prev) => prev.filter((c) => c.id !== id));
        } catch (error) {
            console.error("Failed to delete classification:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, [categories, tasks, skillAssignments]);

    // -----------------------------------------------------------------------
    // Category CRUD
    // -----------------------------------------------------------------------

    const createCategory = useCallback(async (input: CreateCategoryInput) => {
        try {
            const result = await createSkillCategoryMutation({
                classificationId: input.classificationId,
                companyId: input.companyId,
                name: input.name,
                allowMachineNumber: input.allowMachineNumber,
                equipmentCategoryId: input.equipmentCategoryId || null,
            });
            setCategories((prev) => sortByName([...prev, result]));
        } catch (error) {
            console.error("Failed to create category:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const updateCategory = useCallback(async (id: string, name: string, allowMachineNumber?: boolean, classificationId?: string) => {
        try {
            const input: { id: string; name?: string; allowMachineNumber?: boolean; classificationId?: string } = { id, name };
            if (allowMachineNumber !== undefined) {
                input.allowMachineNumber = allowMachineNumber;
            }
            if (classificationId) {
                input.classificationId = classificationId;
            }
            const result = await updateSkillCategoryMutation(input);
            setCategories((prev) =>
                sortByName(prev.map((c) => (c.id === id ? result : c)))
            );
        } catch (error) {
            console.error("Failed to update category:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const deleteCategory = useCallback(async (id: string) => {
        try {
            await deleteSkillCategoryMutation(id);
            setCategories((prev) => prev.filter((c) => c.id !== id));
        } catch (error) {
            console.error("Failed to delete category:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    // -----------------------------------------------------------------------
    // Task CRUD
    // -----------------------------------------------------------------------

    const createTask = useCallback(async (input: CreateTaskInput) => {
        try {
            const result = await createSkillTaskMutation({
                categoryId: input.categoryId,
                companyId: input.companyId,
                name: input.name,
            });
            setTasks((prev) => sortByName([...prev, result]));
        } catch (error) {
            console.error("Failed to create task:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const updateTask = useCallback(async (id: string, name: string, categoryId?: string) => {
        try {
            const input: { id: string; name?: string; categoryId?: string } = { id, name };
            if (categoryId) {
                input.categoryId = categoryId;
            }
            const result = await updateSkillTaskMutation(input);
            setTasks((prev) =>
                sortByName(prev.map((t) => (t.id === id ? result : t)))
            );
        } catch (error) {
            console.error("Failed to update task:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const deleteTask = useCallback(async (id: string) => {
        try {
            await deleteSkillTaskMutation(id);
            setTasks((prev) => prev.filter((t) => t.id !== id));
        } catch (error) {
            console.error("Failed to delete task:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    // -----------------------------------------------------------------------
    // SubCategory CRUD
    // -----------------------------------------------------------------------

    const createSubCategory = useCallback(async (input: CreateSubCategoryInput) => {
        try {
            const result = await createSubCategoryMutation({
                categoryId: input.categoryId,
                companyId: input.companyId,
                name: input.name,
            });
            setSubCategories((prev) => sortByName([...prev, result]));
            return result;
        } catch (error) {
            console.error("Failed to create sub-category:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const updateSubCategory = useCallback(async (id: string, name: string) => {
        try {
            const result = await updateSubCategoryMutation({ id, name });
            setSubCategories((prev) =>
                sortByName(prev.map((sc) => (sc.id === id ? result : sc)))
            );
        } catch (error) {
            console.error("Failed to update sub-category:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const deleteSubCategory = useCallback(async (id: string) => {
        try {
            await deleteSubCategoryMutation(id);
            setSubCategories((prev) => prev.filter((sc) => sc.id !== id));
        } catch (error) {
            console.error("Failed to delete sub-category:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    // -----------------------------------------------------------------------
    // SkillLevel CRUD
    // -----------------------------------------------------------------------

    const createSkillLevel = useCallback(async (input: CreateSkillLevelInput) => {
        try {
            const result = await createSkillLevelMutation({
                companyId: input.companyId,
                name: input.name,
                description: input.description,
                color: input.color,
                sortOrder: input.sortOrder,
            });
            setSkillLevels((prev) => [...prev, result]);
        } catch (error) {
            console.error("Failed to create skill level:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const updateSkillLevel = useCallback(async (id: string, name: string, sortOrder?: number, description?: string, color?: string) => {
        try {
            const input: { id: string; name?: string; sortOrder?: number; description?: string; color?: string } = { id, name };
            if (sortOrder !== undefined) {
                input.sortOrder = sortOrder;
            }
            if (description !== undefined) {
                input.description = description;
            }
            if (color !== undefined) {
                input.color = color;
            }
            const result = await updateSkillLevelMutation(input);
            setSkillLevels((prev) =>
                prev.map((sl) => (sl.id === id ? result : sl))
            );
        } catch (error) {
            console.error("Failed to update skill level:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const deleteSkillLevel = useCallback(async (id: string) => {
        try {
            await deleteSkillLevelMutation(id);
            setSkillLevels((prev) => prev.filter((sl) => sl.id !== id));
        } catch (error) {
            console.error("Failed to delete skill level:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    // -----------------------------------------------------------------------
    // SkillAssignment CRUD
    // -----------------------------------------------------------------------

    const assignSkill = useCallback(async (input: AssignSkillInput) => {
        try {
            const result = await createSkillAssignmentMutation({
                userId: input.userId,
                taskId: input.taskId,
                companyId: input.companyId,
                subCategoryId: input.subCategoryId,
                skillLevel: input.skillLevel,
                evaluatedBy: input.evaluatedBy,
                evaluatedByName: input.evaluatedByName,
                evaluationDate: input.evaluationDate,
                evaluationHistory: input.evaluationHistory,
            });
            setSkillAssignments((prev) => [...prev, result]);
        } catch (error) {
            console.error("Failed to assign skill:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const updateSkillAssignment = useCallback(async (
        id: string,
        updates: Partial<Omit<AssignSkillInput, "userId" | "taskId" | "companyId">>
    ) => {
        try {
            const result = await updateSkillAssignmentMutation({
                id,
                subCategoryId: updates.subCategoryId,
                skillLevel: updates.skillLevel,
                evaluatedBy: updates.evaluatedBy,
                evaluatedByName: updates.evaluatedByName,
                evaluationDate: updates.evaluationDate,
                evaluationHistory: updates.evaluationHistory,
            });
            setSkillAssignments((prev) =>
                prev.map((a) => (a.id === id ? result : a))
            );
        } catch (error) {
            console.error("Failed to update skill assignment:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    const deleteSkillAssignment = useCallback(async (id: string) => {
        try {
            await deleteSkillAssignmentMutation(id);
            setSkillAssignments((prev) => prev.filter((a) => a.id !== id));
        } catch (error) {
            console.error("Failed to delete skill assignment:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    // -----------------------------------------------------------------------
    // UserFile operations (S3 + GraphQL)
    // -----------------------------------------------------------------------

    const uploadUserFile = useCallback(async (userId: string, file: File) => {
        if (!companyId) return;
        const currentUser = companyUser || companyOwner;
        if (!currentUser) return;

        const fileId = uuidv4();
        const s3Path = `public/userFiles/${companyId}/${userId}/${fileId}-${file.name}`;
        const changedBy = currentUser.id;
        const changedByName = (currentUser as any).name || "Unknown";

        try {
            // Upload to S3
            await uploadData({
                path: s3Path,
                data: file,
                options: {
                    contentType: file.type || 'application/octet-stream',
                },
            });

            // Create UserFile record
            const result = await createUserFileMutation({
                userId,
                companyId,
                fileName: file.name,
                s3Path,
                fileSize: file.size,
                uploadedBy: currentUser.id,
                uploadedByName: changedByName,
                uploadedAt: new Date().toISOString(),
            });

            setUserFiles((prev) => [...prev, result]);

            // Auto-generate change log entry for file upload
            const logResult = await createChangeLogEntryMutation({
                userId,
                companyId,
                fieldName: "File Upload",
                oldValue: null,
                newValue: file.name,
                description: `File uploaded — '${file.name}'`,
                changedBy,
                changedByName,
            });
            setChangeLogEntries((prev) => [logResult, ...prev]);
        } catch (error) {
            console.error("Failed to upload user file:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, [companyId, companyUser, companyOwner]);

    const deleteUserFile = useCallback(async (fileId: string, s3Path: string) => {
        const currentUser = companyUser || companyOwner;
        const changedBy = currentUser?.id || "";
        const changedByName = (currentUser as any)?.name || "Unknown";

        // Find the file record to get the file name before deleting
        const fileRecord = userFiles.find((f) => f.id === fileId);
        const fileName = fileRecord?.fileName || "Unknown file";
        const fileUserId = fileRecord?.userId || "";
        const fileCompanyId = fileRecord?.companyId || companyId || "";

        try {
            // Remove from S3
            await remove({ path: s3Path });

            // Delete UserFile record
            await deleteUserFileMutation(fileId);

            setUserFiles((prev) => prev.filter((f) => f.id !== fileId));

            // Auto-generate change log entry for file deletion
            if (fileUserId) {
                const logResult = await createChangeLogEntryMutation({
                    userId: fileUserId,
                    companyId: fileCompanyId,
                    fieldName: "File Deleted",
                    oldValue: fileName,
                    newValue: null,
                    description: `File deleted — '${fileName}'`,
                    changedBy,
                    changedByName,
                });
                setChangeLogEntries((prev) => [logResult, ...prev]);
            }
        } catch (error) {
            console.error("Failed to delete user file:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, [companyId, companyUser, companyOwner, userFiles]);

    const renameUserFile = useCallback(async (fileId: string, newFileName: string) => {
        try {
            await updateUserFileMutation({ id: fileId, fileName: newFileName });
            setUserFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, fileName: newFileName } : f));
        } catch (error) {
            console.error("Failed to rename user file:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    // -----------------------------------------------------------------------
    // ChangeLog
    // -----------------------------------------------------------------------

    const createChangeLogEntry = useCallback(async (input: CreateChangeLogInput) => {
        try {
            const result = await createChangeLogEntryMutation({
                userId: input.userId,
                companyId: input.companyId,
                fieldName: input.fieldName,
                oldValue: input.oldValue,
                newValue: input.newValue,
                description: input.description,
                changedBy: input.changedBy,
                changedByName: input.changedByName,
            });
            setChangeLogEntries((prev) => [result, ...prev]);
        } catch (error) {
            console.error("Failed to create change log entry:", error);
            Sentry.captureException(error);
            throw error;
        }
    }, []);

    // -----------------------------------------------------------------------
    // Context value
    // -----------------------------------------------------------------------

    const value: UserModuleContextValue = {
        classifications,
        categories,
        tasks,
        subCategories,
        skillLevels,
        skillAssignments,
        userFiles,
        changeLogEntries,
        loading,
        fetchSkillAssignments,
        fetchUserFiles,
        fetchChangeLog,
        createClassification,
        updateClassification,
        deleteClassification,
        createCategory,
        updateCategory,
        deleteCategory,
        createTask,
        updateTask,
        deleteTask,
        createSubCategory,
        updateSubCategory,
        deleteSubCategory,
        createSkillLevel,
        updateSkillLevel,
        deleteSkillLevel,
        assignSkill,
        updateSkillAssignment,
        deleteSkillAssignment,
        uploadUserFile,
        deleteUserFile,
        renameUserFile,
        createChangeLogEntry,
    };

    return <UserModuleContext.Provider value={value}>{children}</UserModuleContext.Provider>;
};
